import React from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemIcon from "@mui/material/ListItemIcon";
import CheckCircle from "@mui/icons-material/CheckCircle";
import CircularProgress from "@mui/material/CircularProgress";
import { ConventionLogoSquare } from "../../events/ConventionLogo";
import { formatConventionDate, formatLocation } from "@seabrookstudios/pitch2table-core";
import {
  useAllConventions,
  useGameConventions,
  useAddConventionToGame,
  useShortlistPublisher,
} from "@seabrookstudios/pitch2table";
import { enqueueSnackbar } from "notistack";
import { FullScreenDialog } from "../../core/FullScreenDialog";
import { CircularProgressSize } from "../../theme";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").CalendarEvent} props.convention
 * @param {boolean} props.isLinked
 */
const ConventionListItem = ({ gameId, convention, isLinked }) => {
  const addConvention = useAddConventionToGame(gameId);
  const shortlist = useShortlistPublisher(gameId);

  const onAddConvention = async () => {
    try {
      await addConvention.mutateAsync({ conventionId: convention.id });

      if (convention.type === "PitchEvent" && convention.linkedPublisherId) {
        await shortlist.mutateAsync({ publisherIds: [convention.linkedPublisherId] });
      }
    } catch (error) {
      enqueueSnackbar("Failed to add convention", { variant: "error" });
    }
  };

  return (
    <ListItemButton key={convention.id} onClick={onAddConvention}>
      <ListItemAvatar>
        <ConventionLogoSquare convention={convention} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText
        primary={convention.name}
        secondary={`${formatConventionDate(convention)} • ${formatLocation(convention)}`}
      />
      {isLinked && (
        <ListItemIcon>
          <CheckCircle color="success" />
        </ListItemIcon>
      )}
      {addConvention.isPending && <CircularProgress size={CircularProgressSize} />}
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const AddConventionDialog = ({ gameId }) => {
  const { data: allConventions = [] } = useAllConventions();
  const { data: gameConventions = [] } = useGameConventions(gameId);

  const isConventionLinked = (conventionId) => {
    return gameConventions.some((gc) => gc.id === conventionId);
  };

  return (
    <FullScreenDialog title="Link Convention to Game" canSave={true}>
      <List>
        {allConventions.map((convention) => (
          <ConventionListItem
            key={convention.id}
            convention={convention}
            isLinked={isConventionLinked(convention.id)}
            gameId={gameId}
          />
        ))}
      </List>
      {allConventions.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>No conventions available.</p>
        </div>
      )}
    </FullScreenDialog>
  );
};
